import { ReactComponent as CloseIcon } from '@assets/icons/V4/close-icon.svg';
import { ReactComponent as DiscordIcon } from '@assets/icons/V4/discord-icon.svg';
import { ReactComponent as MailIcon } from '@assets/icons/V4/mail-icon.svg';
import { ReactComponent as PhoneIcon } from '@assets/icons/V4/phone-icon.svg';
import LanguageSwitcher from '@components/V3/LanguageSwitcher';
import Button from '@components/V4/Button';
import IconButton from '@components/V4/IconButton';
import ParentWhatsAppGroupsModal from '@components/WhatsAppGroupsModal/ParentWhatsAppGroupsModal';
import MonthlyNPSModal from '@modules/Base/PendingNPS/MonthlyNPSModal';
import {
  SUPPORT_UPGRADE_EDUCATION_EMAIL,
  DISCORD_STUDENT_CHANNEL,
  UPGRADE_EDUCATION_PHONE_NUMBER,
  UPGRADE_EDUCATION_PHONE_NUMBER_DISPLAY,
} from '@shared/constants';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../../../TranslationContext';
import ParentPageUniversitiesApplyingTo from './components/ParentPageAside/components/ParentPageUniversitiesApplyingTo';
import ParentPageDesktopView from './components/ParentPageDesktopView';
import ParentPageEnrollmentFeedbackModal from './components/ParentPageEnrollmentFeedbackModal copy/ParentPageEnrollmentFeedbackModal';
import ParentPageFeedbackSection from './components/ParentPageFeedbackSection';
import ParentPageHeader from './components/ParentPageHeader/ParentPageHeader';
import ParentPageMobileView from './components/ParentPageMobileView';
import ParentPageTimeline from './components/ParentPageTimeline/ParentPageTimeline';
import useParentNpsModal from './hooks/useParentNpsModal';

const ParentPage = () => {
  const { t, language } = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();

  const firstUpgradeNPS = Math.random() < 0.5;

  const [parentEnrollmentFeedbackModalSessionId, setParentEnrollmentFeedbackModalSessionId] =
    useState<string | undefined>(undefined);
  const [isContactMenuOpen, setIsContactMenuOpen] = useState(false);
  // get token from url params
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const storedToken = localStorage.getItem('parentToken');
  const sessionIdFromUrl = urlParams.get('sessionId');
  const parentToken = token || storedToken || undefined;

  const { isNpsModalOpen } = useParentNpsModal({
    studentId,
    token: parentToken,
  });

  if (storedToken !== token && token) {
    localStorage.setItem('parentToken', token);
  }

  if (!storedToken && token) {
    if (token) {
      localStorage.setItem('parentToken', token);
    }
  }

  if (!token && !storedToken) {
    window.location.href = '/error';
  }

  const openParentEnrollmentFeedbackModalHandler = (sessionId: string) => {
    setParentEnrollmentFeedbackModalSessionId(sessionId);
  };

  const closeParentEnrollmentFeedbackModalHandler = () => {
    setParentEnrollmentFeedbackModalSessionId(undefined);
  };

  const getInTouchClickHandler = () => {
    setIsContactMenuOpen((prev) => !prev);
  };

  const contactPoints = [
    {
      icon: <PhoneIcon className="text-inherit" />,
      text: UPGRADE_EDUCATION_PHONE_NUMBER_DISPLAY,
      href: `tel:${UPGRADE_EDUCATION_PHONE_NUMBER}`,
    },
    {
      icon: <MailIcon className="text-inherit" />,
      text: SUPPORT_UPGRADE_EDUCATION_EMAIL,
      href: `mailto:${SUPPORT_UPGRADE_EDUCATION_EMAIL}`,
    },
    {
      icon: <DiscordIcon className="text-inherit" />,
      text: t('contact.chat_discord'),
      href: DISCORD_STUDENT_CHANNEL,
    },
  ];

  useEffect(() => {
    if (sessionIdFromUrl) {
      openParentEnrollmentFeedbackModalHandler(sessionIdFromUrl);
      const urlParams = new URLSearchParams(window.location.search);
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl.endsWith('?') ? newUrl.slice(0, -1) : newUrl);
    }
  }, [sessionIdFromUrl]);

  return (
    <section className="bg-surfaceBackground min-h-screen min-w-screen">
      <div className="mx-4 flex flex-col gap-6 max-w-[1300px] laptop:m-auto laptop:py-8 laptop:gap-8 laptop:px-4">
        <div className="flex flex-col items-center justify-between tablet:flex-row">
          <div className="flex justify-between w-full tablet:hidden">
            <div className="flex items-center justify-between w-full gap-4 tablet:items-center">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Button
                    onClick={getInTouchClickHandler}
                    buttonText={t('need_help')}
                    variant="primary"
                  />

                  {isContactMenuOpen && (
                    <div className="absolute flex-col gap-6 pt-8 pb-12 px-12 rounded-lg bg-contactPopup z-9999 shadow-contactPopup top-[calc(100%+4px)] left-0 w-max max-w-[421px]">
                      <div className="flex flex-col gap-2">
                        <div className="flex justify-between">
                          <h2 className="font font-raleway font-semibold leading-8 text-2xl">
                            {t('contact.get_in_touch')}
                          </h2>
                          <IconButton onClick={getInTouchClickHandler} icon={<CloseIcon />} />
                        </div>
                        <p className="text-customGrey text-sm">{t('contact.help_message')}</p>
                      </div>

                      <div className="flex flex-col gap-4">
                        {contactPoints.map((contact) => (
                          <a
                            key={contact.href}
                            className="flex items-center gap-4 text-white hover:text-customLila"
                            href={contact.href}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {contact.icon}
                            <span className="font-raleway font-bold ">{contact.text}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <LanguageSwitcher />
            </div>
          </div>
          <h1 className="font-raleway font-semibold text-2xl w-full tablet:w-auto">
            {t('parent_dashboard')}
          </h1>
          <div className="hidden items-center gap-4 tablet:flex">
            <div className="relative flex items-center gap-4">
              <a
                target="_blank"
                rel="noreferrer"
                href="https://drive.google.com/drive/folders/14ewSz8vNOxMoIl4LtMGhMe4k3p1zVv8B"
              >
                <Button buttonText={t('resources')} variant="secondary" />
              </a>
              <Button
                onClick={getInTouchClickHandler}
                buttonText={t('need_help')}
                variant="primary"
              />

              {isContactMenuOpen && (
                <div className="absolute flex-col gap-6 pt-8 pb-12 px-12 rounded-lg bg-contactPopup z-9999 shadow-contactPopup top-[calc(100%+4px)] right-0 w-max max-w-[421px]">
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between">
                      <h2 className="font font-raleway font-semibold leading-8 text-2xl">
                        {t('contact.get_in_touch')}
                      </h2>
                      <IconButton onClick={getInTouchClickHandler} icon={<CloseIcon />} />
                    </div>
                    <p className="text-customGrey text-sm">{t('contact.help_message')}</p>
                  </div>

                  <div className="flex flex-col gap-4">
                    {contactPoints.map((contact) => (
                      <a
                        key={contact.href}
                        className="flex items-center gap-4 text-white hover:text-customLila"
                        href={contact.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {contact.icon}
                        <span className="font-raleway font-bold ">{contact.text}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <LanguageSwitcher />
          </div>
        </div>

        <ParentPageHeader />
        <ParentPageMobileView />
        <ParentPageDesktopView />
        <ParentPageTimeline />
        <ParentPageUniversitiesApplyingTo />
        <ParentPageFeedbackSection />

        {!isNpsModalOpen && <ParentWhatsAppGroupsModal studentId={studentId} token={parentToken} />}

        {isNpsModalOpen && (
          <MonthlyNPSModal
            visible={true}
            firstUpgradeNPS={firstUpgradeNPS}
            parentView={true}
            allowClose={false}
          />
        )}

        {parentEnrollmentFeedbackModalSessionId && (
          <ParentPageEnrollmentFeedbackModal
            sessionId={parentEnrollmentFeedbackModalSessionId}
            onClose={closeParentEnrollmentFeedbackModalHandler}
            language={language}
          />
        )}
      </div>
    </section>
  );
};

export default ParentPage;
